import * as React from 'react';
import { Button, Grid, makeStyles, Theme } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { append, remove } from 'ramda';
import nl2br from 'nl2br';
import {
  ChoiceButtonOnClickProps,
  ChoiceValue,
  MultiChoiceProps,
  Choice,
} from './ElementTypeInterfaces';
import classNames from 'class-names';

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    flexGrow: 2,
    flexBasis: 0,
  },
  squareButton: {
    flexGrow: 2,
    flexBasis: 0,
    borderRadius: 0,
    margin: `${theme.spacing()}px 0`,
    textTransform: 'none',
    paddingLeft: 0,
    height: '40px',
  },
  checkButton: {
    boxShadow: 'none',
    margin: `-${theme.spacing()}px 0`,
    borderRadius: 0,
    flexGrow: 1,
    maxWidth: 40,
    borderRight: '1px solid rgba(0, 0, 0, 0.23)',
  },
  checkButtonSelected: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    height: '40px',
    borderRight: 'none',
  },
  indexButton: {
    boxShadow: 'none',
    borderRadius: 0,
    flexGrow: 1,
    maxWidth: 40,
    margin: `${theme.spacing()}px`,
    marginRight: 0,
  },
  indexButtonSelected: {
    color: '#000000 !important',
    borderColor: '#000000',
  },
  root: {
    maxWidth: 800,
    width: '100%',
  },
}));

interface MultiChoiceButtonProps {
  choice: Choice;
  onChange: (value) => void;
  selectedIndex: number;
  showOrder: boolean;
  values?: number[];
}

const isDisable = (choice: Choice, values: number[] = []): boolean => {
  if (choice.value === 999) {
    return values.length > 0 && values[0] !== 999;
  }
  return values.length > 0 && values[0] === 999;
};

const ChoiceButton: React.FunctionComponent<
  ChoiceButtonOnClickProps & MultiChoiceButtonProps
> = ({ choice, values, onChange, showOrder, selectedIndex }) => {
  const classes = useStyles();

  const selected = selectedIndex !== -1;

  const dataTestButtonTitle = `multiChoiceButton${choice.value}`;

  const onClick = () => {
    onChange(choice.value);
  };

  return (
    <Grid
      container
      direction="row"
      key={choice.value}
      justify="space-evenly"
      alignItems="stretch">
      <Button
        data-test-id={dataTestButtonTitle}
        disabled={isDisable(choice, values)}
        onClick={onClick}
        className={classes.squareButton}
        variant="outlined"
        color={selected ? 'primary' : 'default'}>
        <Grid
          container
          direction="row"
          key={choice.value}
          justify="space-evenly"
          alignItems="center">
          <Button
            component={'div'}
            style={{ backgroundColor: 'transparent' }}
            className={classNames(classes.checkButton, {
              [classes.checkButtonSelected]: selected,
            })}>
            {selected ? <CheckIcon style={{ color: '#000' }} /> : ''}
          </Button>
          <span
            className={classes.label}
            dangerouslySetInnerHTML={{ __html: nl2br(choice.text) }}
          />
        </Grid>
      </Button>
      {showOrder ? (
        <Button
          variant="outlined"
          disabled
          className={classNames(classes.indexButton, {
            [classes.indexButtonSelected]: selected,
          })}>
          {!selected ? '#' : selectedIndex + 1}
        </Button>
      ) : (
        ''
      )}
    </Grid>
  );
};

const MultiChoiceInner: React.FunctionComponent<MultiChoiceProps> = ({
  choices,
  showOrder = false,
  value = [] as number[],
  onChange,
}) => {
  const classes = useStyles();

  const onChangeHandler = (val: ChoiceValue) => {
    const index = value.indexOf(val);
    if (index !== -1) {
      onChange(remove(index, 1, value));
    } else {
      onChange(append<ChoiceValue>(val, value));
    }
  };

  return (
    <div className={classes.root}>
      {choices.map((choice) => (
        <ChoiceButton
          key={choice.value}
          selectedIndex={value.indexOf(choice.value)}
          values={value}
          choice={choice}
          onChange={onChangeHandler}
          showOrder={showOrder}
        />
      ))}
    </div>
  );
};

export default MultiChoiceInner;
